<script lang="ts" setup>
import { HttpStatusCode } from "axios";
import type { FunctionalComponent, SVGAttributes } from "vue";
import { useDownloadPlatformIdCard } from "~/core/composables/useDownloadPlatformIdCard";
import type LxcError from "~/core/models/LxcError.class";
import { useUploadLicense } from "~/modules/license/composables/useUploadLicense";
import { LicenseUploadContext } from "~/modules/license/models/LicenseUploadContext.enum";
import ILxcAlertTriangle from "~icons/lxc/alert-triangle";
import ILxcDownload from "~icons/lxc/download";
import ILxcInfo from "~icons/lxc/info";
import ILxcUpload from "~icons/lxc/upload";

const props = defineProps<{
  organizationCode?: number;
  isLoading?: boolean;
  isExpired?: boolean;
  error?: LxcError;
}>();

const emit = defineEmits(["uploadSuccess", "uploadError"]);

const { t } = useI18n();

const { canUploadLicense } = useUploadLicense();

const {
  downloadPlatformIdCard,
  isLoading: isLoadingDownloadIdCard,
  canDownloadPlatformIdCard,
} = useDownloadPlatformIdCard({
  organizationCode: props.organizationCode,
});

const onShowUploadModal = () => {
  showUploadModal.value = true;
};

const isUploadingLicense = ref(false);

const showUploadModal = ref(false);

const iconClass = computed(() => {
  const iconClass = ["p-2", "border-8", "rounded-full", "box-border"];
  return [...iconClass, ...messageConfiguration.value.iconColorTheme];
});

interface MessageConfiguration {
  title: string;
  message: string;
  icon: FunctionalComponent<SVGAttributes>;
  iconColorTheme: string[];
}

const messageConfiguration: ComputedRef<MessageConfiguration> = computed(() => {
  if (props.error?.status === HttpStatusCode.NotFound) {
    return {
      title: "license.overlay.title.notFound",
      message: "license.overlay.message.notFound",
      icon: ILxcInfo,
      iconColorTheme: [
        "bg-warning-100",
        "text-warning-700",
        "border-warning-50",
      ],
    };
  } else if (props.isExpired) {
    return {
      title: "license.overlay.title.expired",
      message: "license.overlay.message.expired",
      icon: ILxcAlertTriangle,
      iconColorTheme: ["bg-error-100", "text-error-700", "border-error-50"],
    };
  } else {
    return {
      title: props.error?.title ?? "errors.httpStatusCodes.500.title",
      message: props.error?.detail ?? "errors.httpStatusCodes.500.subtitle",
      icon: ILxcAlertTriangle,
      iconColorTheme: ["bg-error-100", "text-error-700", "border-error-50"],
    };
  }
});

const downloadIdCardButtonTitle = computed(() => {
  if (props.error?.status === HttpStatusCode.NotFound) {
    return t("license.idCard.download");
  } else {
    return t("license.idCard.export.text");
  }
});
</script>

<template>
  <div class="w-full h-full flex flex-col justify-center items-center">
    <template v-if="isLoading || isUploadingLicense">
      <lxc-loader size="16" />
      <span class="mt-5">{{ t("license.upload.loading") }}</span>
    </template>
    <template v-else>
      <div :class="iconClass">
        <component :is="messageConfiguration.icon" />
      </div>
      <h2 class="mt-4">{{ t(messageConfiguration?.title) }}</h2>
      <p class="w-1/3 font-medium text-base text-center">
        {{ t(messageConfiguration?.message) }}
      </p>
      <div class="flex gap-3 mt-4">
        <lxc-button
          v-if="canUploadLicense()"
          :title="t('license.upload.text')"
          :icon="ILxcUpload"
          :text="t('license.upload.text')"
          @click="onShowUploadModal"
        />
        <lxc-button
          v-if="canDownloadPlatformIdCard()"
          :title="downloadIdCardButtonTitle"
          :text="downloadIdCardButtonTitle"
          type="secondary"
          :icon="ILxcDownload"
          :is-loading="isLoadingDownloadIdCard"
          @click="downloadPlatformIdCard"
        />
      </div>
    </template>
  </div>

  <license-upload-modal
    v-model:show="showUploadModal"
    :organization-code="organizationCode"
    :upload-context="LicenseUploadContext.OVERLAY"
    @is-loading="isUploadingLicense = $event"
    @success="emit('uploadSuccess')"
    @error="emit('uploadError', $event)"
  />
</template>
